import * as React from 'react'

import { Command as CommandPrimitive } from 'cmdk'

import SearchIcon from '@/assets/icons/icon-search.svg'
import { classNames } from '@/utils/helpers.ts'

const Command = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={classNames(
      'bg-popover text-popover-foreground flex h-full w-full flex-col overflow-hidden rounded-md',
      className,
    )}
    {...props}
  />
))

Command.displayName = CommandPrimitive.displayName

const CommandInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div
    className="flex items-center border-b px-3"
    // eslint-disable-next-line react/no-unknown-property
    cmdk-input-wrapper=""
  >
    <SearchIcon className="text-secondary-200 mr-2 h-4 w-4 shrink-0 opacity-50 dark:text-white" />
    <CommandPrimitive.Input
      ref={ref}
      className={classNames(
        'placeholder:text-muted-foreground flex h-10 w-full rounded-md py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-inherit dark:text-white dark:caret-white',
        className,
      )}
      {...props}
    />
  </div>
))

CommandInput.displayName = CommandPrimitive.Input.displayName

const CommandPlainInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div
    className="flex items-center border-b"
    // eslint-disable-next-line react/no-unknown-property
    cmdk-input-wrapper=""
  >
    <CommandPrimitive.Input
      ref={ref}
      className={classNames(
        'border-input placeholder:text-muted-foreground flex h-9 w-full rounded-md border bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:shadow focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:text-white',
        className,
      )}
      {...props}
    />
  </div>
))

CommandPlainInput.displayName = CommandPrimitive.Input.displayName

const CommandSearchBarInput = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Input>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => {
  const isWindows = navigator.userAgent.includes('Win')

  return (
    <div
      className="bg-card focus-within:border-primary-500 flex items-center rounded-md border px-3 py-1 font-medium"
      // eslint-disable-next-line react/no-unknown-property
      cmdk-input-wrapper=""
    >
      <SearchIcon className="text-secondary-200 mr-2 h-4 w-4 shrink-0 opacity-50 dark:text-white" />
      <CommandPrimitive.Input
        ref={ref}
        className={classNames(
          'placeholder:text-muted-foreground flex h-10 w-full rounded-md py-3 text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:bg-inherit dark:text-white dark:caret-white',
          className,
        )}
        {...props}
      />

      <kbd className="bg-muted text-muted-foreground pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border px-1.5 font-mono text-[10px] font-medium opacity-100">
        <span className="text-xs">{isWindows ? 'Ctrl+' : '⌘'}</span>K
      </kbd>
    </div>
  )
})

CommandSearchBarInput.displayName = CommandPrimitive.Input.displayName

const CommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={classNames(
      'max-h-[300px] overflow-y-auto overflow-x-hidden',
      className,
    )}
    {...props}
  />
))

CommandList.displayName = CommandPrimitive.List.displayName

const CommandEmpty = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Empty>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-sm dark:text-white/50"
    {...props}
  />
))

CommandEmpty.displayName = CommandPrimitive.Empty.displayName

const CommandGroup = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Group>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={classNames(
      'text-foreground [&_[cmdk-group-heading]]:text-muted-foreground overflow-hidden p-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
      className,
    )}
    {...props}
  />
))

CommandGroup.displayName = CommandPrimitive.Group.displayName

const CommandSeparator = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={classNames('bg-border -mx-1 h-px', className)}
    {...props}
  />
))

CommandSeparator.displayName = CommandPrimitive.Separator.displayName

const CommandItem = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={classNames(
      "aria-selected:text-accent-foreground hover:bg-neutral-contrast aria-selected:bg-neutral-contrast dark:hover:bg-secondary-600 dark:aria-selected:bg-secondary-600 relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none data-[disabled='true']:pointer-events-none data-[disabled='true']:opacity-50 dark:text-white",
      className,
    )}
    {...props}
  />
))

CommandItem.displayName = CommandPrimitive.Item.displayName

const CommandShortcut = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLSpanElement>) => {
  return (
    <span
      className={classNames(
        'text-muted-foreground ml-auto text-xs tracking-widest',
        className,
      )}
      {...props}
    />
  )
}

CommandShortcut.displayName = 'CommandShortcut'

export {
  Command,
  CommandInput,
  CommandSearchBarInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
  CommandPlainInput,
}
